import { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DocumentPortalScanElement } from '../DocumentPortalScanElement';
import { ILegalTransaction } from '../../Interfaces/ILegalTransaction';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';
import { ILegalTransactionTransactionNumber } from '../../Interfaces/ILegalTransactionTransactionNumber';
import { ExpandMore } from '@mui/icons-material';
import { IFSScanResult } from '../../Interfaces/IFSDirectory';
import { DocumentPortalAccordionSkeleton } from './DocumentPortalAccordionSkeleton';
import { DocumentSortingDialog } from '../DocumentSortingDialog/DocumentSortingDialog';
import { useGetFSSortedDocumentsWithFilter } from '../../hooks/reactQuerys/DocumentQueries';

const options: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

interface IProps {
  currentDate: Date;
  fsScanResults: IFSScanResult[];
  legalTransactions: ILegalTransaction[];
  legalReqStates: ILegalRequirementState[];
  recordNumbers: ILegalTransactionRecordNumber[];
  transactionNumbers: ILegalTransactionTransactionNumber[];
  openAsOpen?: boolean;
}

export const DocumentPortalAccordion = ({
  currentDate,
  fsScanResults,
  legalTransactions,
  legalReqStates,
  recordNumbers,
  transactionNumbers,
  openAsOpen,
}: IProps) => {
  const [localFsScanResults, setLocalFsScanResults] =
    useState<IFSScanResult[]>(fsScanResults);

  const { fsSortedDocuments, isLoadingFSSortedDocuments } =
    useGetFSSortedDocumentsWithFilter(localFsScanResults);

  const [isOpen, setIsOpen] = useState(
    openAsOpen ||
      localFsScanResults.filter(
        localFsScanResult => !localFsScanResult.isSorted
      ).length > 0
  );

  const [selectedFsScanResult, setSelectedFsScanResult] =
    useState<IFSScanResult | null>(null);

  /* This is a workaround, because loading the updated data from the backend takes too long. 
  Because of this, we update the data locally, but we still want to overwrite the local data with data from the backend if needed.
  This prevents the client data from being stale, and not being properly updated, when new data comes in from the backend.
  */
  useEffect(() => {
    if (fsScanResults) {
      setLocalFsScanResults(fsScanResults);
    }
  }, [fsScanResults]);

  const handleOpenSortingDialog = (fsScanResult: IFSScanResult) => {
    setSelectedFsScanResult(fsScanResult);
  };

  const handleGoToNextDocument = () => {
    if (!selectedFsScanResult) {
      return;
    }

    // Get the index of the currently selected document
    const currentIndex = localFsScanResults.findIndex(
      fsScanResult =>
        fsScanResult.fileName === selectedFsScanResult.fileName &&
        fsScanResult.idFSDirectory === selectedFsScanResult.idFSDirectory
    );

    // Get the indices of all unsorted documents
    const unsortedIndices = localFsScanResults
      .filter(fsScanResult => !fsScanResult.isSorted)
      .map(fsScanResult => localFsScanResults.indexOf(fsScanResult));

    // Get the next index of the closest unsorted document
    const nextIndex = unsortedIndices.find(index => index > currentIndex);

    // If there is no next index, wrap around to the first unsorted document
    if (!nextIndex) {
      setSelectedFsScanResult(localFsScanResults[unsortedIndices[0]]);
    } else {
      setSelectedFsScanResult(localFsScanResults[nextIndex]);
    }
  };

  const handleGoToPreviousDocument = () => {
    if (!selectedFsScanResult) return;

    // Get the index of the currently selected document
    const currentIndex = localFsScanResults.findIndex(
      fsScanResult =>
        fsScanResult.fileName === selectedFsScanResult.fileName &&
        fsScanResult.idFSDirectory === selectedFsScanResult.idFSDirectory
    );

    // Get the indices of all unsorted documents
    const unsortedIndices = localFsScanResults
      .filter(fsScanResult => !fsScanResult.isSorted)
      .map(fsScanResult => localFsScanResults.indexOf(fsScanResult));

    // Get the previous unsorted document index
    const currentUnsortedIndex = unsortedIndices.findIndex(
      index => index === currentIndex
    );

    // Get the actual array index of the previous unsorted document
    const prevIndex =
      unsortedIndices[
        (currentUnsortedIndex - 1 + unsortedIndices.length) %
          unsortedIndices.length
      ];

    setSelectedFsScanResult(localFsScanResults[prevIndex]);
  };

  if (isLoadingFSSortedDocuments && !localFsScanResults) {
    return <DocumentPortalAccordionSkeleton isOpen={isOpen} />;
  }

  return (
    <>
      <Accordion
        variant='outlined'
        expanded={isOpen}
        onChange={() => setIsOpen(!isOpen)}
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography sx={{ width: '33%', flexShrink: 0 }}>
            {currentDate.toLocaleDateString('de-DE', options)}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {
              localFsScanResults.filter(fsScanResult => fsScanResult.isSorted)
                .length
            }
            &nbsp;von&nbsp;
            {localFsScanResults.length}
            &nbsp;sortiert
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {localFsScanResults &&
          localFsScanResults.length !== 0 &&
          fsSortedDocuments ? (
            <Grid sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ flex: 1 }}>Dokument</TableCell>
                    <TableCell sx={{ width: 100 }}>Status</TableCell>
                    <TableCell sx={{ width: 200 }}>Urkundennummer</TableCell>
                    <TableCell sx={{ width: 200 }}>Vorgangsnummer</TableCell>
                    <TableCell sx={{ flex: 1 }}>Ziel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {localFsScanResults.map(fsScanResult => (
                    <DocumentPortalScanElement
                      key={`document-scan-${fsScanResult.idFSDirectory}-${fsScanResult.fileName}`}
                      fsScanObject={fsScanResult}
                      setFsScanResults={setLocalFsScanResults}
                      fsDirSortedDocuments={fsSortedDocuments}
                      legalTransactions={legalTransactions}
                      recordNumbers={recordNumbers}
                      transactionNumbers={transactionNumbers}
                      onOpenSortingDialog={handleOpenSortingDialog}
                    />
                  ))}
                </TableBody>
              </Table>
            </Grid>
          ) : (
            <Typography>Keine Dokumente gefunden</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {selectedFsScanResult && (
        <DocumentSortingDialog
          open={!!selectedFsScanResult}
          onClose={() => setSelectedFsScanResult(null)}
          fsScanResult={selectedFsScanResult}
          legalTransactions={legalTransactions}
          recordNumbers={recordNumbers}
          transactionNumbers={transactionNumbers}
          legalReqStates={legalReqStates}
          onGoToNextDocument={handleGoToNextDocument}
          onGoToPreviousDocument={handleGoToPreviousDocument}
          fsScanResults={localFsScanResults}
          fsDirSortedDocuments={fsSortedDocuments || []}
          setFsScanResults={setLocalFsScanResults}
        />
      )}
    </>
  );
};
